/*
 * quote-list.js
 *
 * A persistent, client-side "Quote list" that runs in parallel to the native
 * Shopify cart. Buying and quoting are two peer motions: the cart is for
 * "I want to pay now", the quote list is for "quote me, I'm not ready to buy".
 *
 * Built on localStorage (repurposed from the retired wishlist). Because a quote
 * never shows a price, the list stores only gate-safe display data captured at
 * add-time (handle, title, image, SKU, quantity) — it never fetches product
 * JSON, so gated (Net) prices never reach the browser through this feature.
 *
 * Surfaces it drives:
 *   - [data-add-to-quote]        add/increment buttons (PDP + product cards)
 *   - [data-quote-count]         header counter (+ [data-quote-count-bubble])
 *   - [data-quote-list-page]     the quote page line-item list
 *   - [data-quote-form]          the request form (fills a hidden field on submit)
 *   - [data-quote-clear]         present on the post-submit success page
 */
(function () {
  var STORAGE_KEY = 'dropsafe-quote';
  var EVENT = 'dropsafe-quote:updated';

  /* ------------------------------------------------------------------ store */
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    if (list.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    else localStorage.removeItem(STORAGE_KEY);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { list: list } }));
  }

  function count() {
    return read().reduce(function (sum, item) {
      return sum + (parseInt(item.qty, 10) || 0);
    }, 0);
  }

  function addItem(item) {
    var list = read();
    var existing = list.filter(function (i) {
      return i.handle === item.handle;
    })[0];
    if (existing) existing.qty = (parseInt(existing.qty, 10) || 0) + item.qty;
    else list.push(item);
    write(list);
  }

  function setQty(handle, qty) {
    var list = read();
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) {
        list[i].qty = qty > 0 ? qty : 1;
        break;
      }
    }
    write(list);
  }

  function removeItem(handle) {
    write(
      read().filter(function (i) {
        return i.handle !== handle;
      })
    );
  }

  function clear() {
    write([]);
  }

  /* -------------------------------------------------------------- add buttons */
  function qtyFor(btn) {
    var wrap = btn.closest('[data-add-to-quote-wrapper]');
    var input = wrap && wrap.querySelector('[data-quote-qty]');
    if (!input) {
      var scope = btn.closest('.product-card, product-info, [data-quote-scope]');
      input = scope && scope.querySelector('input[name="quantity"]');
    }
    var v = input ? parseInt(input.value, 10) : 1;
    return v && v > 0 ? v : 1;
  }

  function flashAdded(btn) {
    var label = btn.querySelector('[data-quote-label]');
    if (!label) return;
    var added = btn.getAttribute('data-added-label');
    if (!added || btn.dataset.flashing) return;
    var original = label.textContent;
    btn.dataset.flashing = '1';
    label.textContent = added;
    btn.classList.add('is-added');
    setTimeout(function () {
      label.textContent = original;
      btn.classList.remove('is-added');
      delete btn.dataset.flashing;
    }, 1600);
  }

  function onAddClick(btn) {
    addItem({
      handle: btn.getAttribute('data-handle'),
      title: btn.getAttribute('data-title') || '',
      image: btn.getAttribute('data-image') || '',
      sku: btn.getAttribute('data-sku') || '',
      qty: qtyFor(btn)
    });
    flashAdded(btn);
    if (window.dataLayer) {
      window.dataLayer.push({ event_name: 'add_to_quote', event_category: 'quote', event_action: 'add' });
    }
  }

  /* --------------------------------------------------------------- rendering */
  function updateCounts() {
    var c = count();
    document.querySelectorAll('[data-quote-count]').forEach(function (el) {
      el.textContent = c;
    });
    document.querySelectorAll('[data-quote-count-bubble]').forEach(function (el) {
      el.hidden = c === 0;
    });
  }

  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str == null ? '' : str;
    return d.innerHTML;
  }

  // Trash icon mirrored from snippets/render-icons.liquid ('remove') so the
  // quote rows use the same affordance as the cart line items.
  var REMOVE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" width="16" height="16">' +
    '<path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/>' +
    '<path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"/>' +
    '</svg>';

  function renderRows(ul, list) {
    var skuLabel = ul.dataset.skuLabel || '';
    var qtyLabel = ul.dataset.qtyLabel || '';
    var removeLabel = ul.dataset.removeLabel || 'Remove';

    ul.innerHTML = list
      .map(function (item) {
        var img = item.image
          ? '<img class="quote-row__img" src="' + esc(item.image) + '" alt="" width="64" height="64">'
          : '<span class="quote-row__img"></span>';
        var sku = item.sku
          ? '<span class="quote-row__sku">' + esc(skuLabel) + ' ' + esc(item.sku) + '</span>'
          : '';
        var id = 'quote-qty-' + esc(item.handle);
        return (
          '<li class="quote-row" data-quote-row data-handle="' + esc(item.handle) + '">' +
          img +
          '<div class="quote-row__info">' +
          '<span class="quote-row__title">' + esc(item.title) + '</span>' +
          sku +
          '</div>' +
          '<div class="quote-row__controls">' +
          '<label for="' + id + '">' + esc(qtyLabel) + '</label>' +
          '<input id="' + id + '" class="quote-qty" type="number" min="1" inputmode="numeric" value="' +
          (parseInt(item.qty, 10) || 1) + '" data-quote-row-qty>' +
          '<button type="button" class="quote-remove" aria-label="' + esc(removeLabel) + '" data-quote-remove>' +
          REMOVE_ICON +
          '</button>' +
          '</div>' +
          '</li>'
        );
      })
      .join('');
  }

  // On the post-submit success page we only show the confirmation: the list
  // page is hidden and the form wrapper (holding the success message) stays
  // visible while the list is cleared.
  function isSuccessPage() {
    return !!document.querySelector('[data-quote-clear]');
  }

  function renderLists() {
    if (isSuccessPage()) return;
    var list = read();
    var hasItems = list.length > 0;

    document.querySelectorAll('[data-quote-empty]').forEach(function (el) {
      el.hidden = hasItems;
    });
    document.querySelectorAll('[data-quote-filled]').forEach(function (el) {
      el.hidden = !hasItems;
    });
    document.querySelectorAll('[data-quote-form-wrapper]').forEach(function (el) {
      el.hidden = !hasItems;
    });
    document.querySelectorAll('[data-quote-lines]').forEach(function (ul) {
      renderRows(ul, list);
    });
  }

  /* ------------------------------------------------------------ form submit */
  // Quantity, title and SKU only. No pricing-review note: a human writes the
  // quote from this email anyway, so the flag added nothing and leaked into
  // the sales team's reply to the customer.
  function buildSummary() {
    return read()
      .map(function (item) {
        var line = (parseInt(item.qty, 10) || 1) + ' x ' + item.title;
        if (item.sku) line += ' [SKU: ' + item.sku + ']';
        return line;
      })
      .join('\n');
  }

  function wireForm(form) {
    form.addEventListener('submit', function () {
      var field = form.querySelector('[name="contact[Products requested]"]');
      if (field) field.value = buildSummary();
    });
  }

  /* -------------------------------------------------------------- delegation */
  function initListeners() {
    document.addEventListener('click', function (e) {
      var addBtn = e.target.closest('[data-add-to-quote]');
      if (addBtn) {
        e.preventDefault();
        onAddClick(addBtn);
        return;
      }
      var removeBtn = e.target.closest('[data-quote-remove]');
      if (removeBtn) {
        var row = removeBtn.closest('[data-quote-row]');
        if (row) removeItem(row.getAttribute('data-handle'));
      }
    });

    document.addEventListener('change', function (e) {
      var qtyInput = e.target.closest('[data-quote-row-qty]');
      if (qtyInput) {
        var row = qtyInput.closest('[data-quote-row]');
        if (row) setQty(row.getAttribute('data-handle'), parseInt(qtyInput.value, 10) || 1);
      }
    });
  }

  function init() {
    initListeners();
    document.querySelectorAll('[data-quote-form]').forEach(wireForm);

    if (isSuccessPage()) {
      // Show only the confirmation, then clear the handed-off list.
      document.querySelectorAll('[data-quote-list-page]').forEach(function (el) {
        el.hidden = true;
      });
      document.querySelectorAll('[data-quote-form-wrapper]').forEach(function (el) {
        el.hidden = false;
      });
      clear();
      updateCounts();
      return;
    }

    updateCounts();
    renderLists();
  }

  document.addEventListener(EVENT, function () {
    updateCounts();
    renderLists();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
